import {
  GenericImportPage,
  StepFinishImportLocation,
  StepInitAnalyzeUrl,
  StepperProvider,
  defaultStepper,
} from '@agilelab/plugin-wb-catalog-import';
import { WbBreadcrumb, WbBreadcrumbs } from '@agilelab/plugin-wb-platform';
import { StepLabel, Typography } from '@material-ui/core';
import pluralize from 'pluralize';
import { useMemo } from 'react';
import { useLocation } from 'react-router';

const KIND_TO_IMPORT = [
  'EditTemplate',
  'Template',
  'Blueprint',
  'ReverseProvisioningTemplate',
  'SystemPrototypeTemplate',
];

export const BlueprintTemplateImport = () => {
  const location = useLocation();
  const historyLabel = pluralize(location.state.label);
  const historyRef = location.state.breadcrumbRef;

  const stepper: StepperProvider = useMemo(
    () => ({
      ...defaultStepper,
      analyze: (state, { apis }) => ({
        stepLabel: <StepLabel>Select URL</StepLabel>,
        content: (
          <StepInitAnalyzeUrl
            key="analyze"
            analysisUrl={state.analysisUrl}
            onAnalysis={state.onAnalysis}
            disablePullRequest={!apis.catalogImportApi.preparePullRequest}
            exampleLocationUrl="https://github.com/agile-lab-dev/witboost-data-product-template/blob/master/template.yaml"
            urlLabel="Template URL"
            urlHelperText="Enter the full path to your YAML template file to register your template"
            locationKindsToImport={[...KIND_TO_IMPORT, 'Location']}
          />
        ),
      }),
      finish: state => ({
        stepLabel: <StepLabel>Finish</StepLabel>,
        content: (
          <StepFinishImportLocation
            prepareResult={state.prepareResult}
            onReset={state.onReset}
            redirectLabel={`Back to ${historyLabel} list page`}
            redirectRoute={historyRef}
          />
        ),
      }),
    }),
    [historyLabel, historyRef],
  );

  return (
    <GenericImportPage
      pageTitle="Register Blueprint and Template"
      breadcrumbs={
        <WbBreadcrumbs aria-label="breadcrumb">
          <WbBreadcrumb>Builder</WbBreadcrumb>
          <WbBreadcrumb to={historyRef}>{historyLabel}</WbBreadcrumb>
          <WbBreadcrumb>Register Template</WbBreadcrumb>
        </WbBreadcrumbs>
      }
      cardTitle={({ appTitle }) =>
        `Start tracking your template in ${appTitle}`
      }
      importInfo={({ appTitle }) => (
        <>
          <Typography variant="h6" paragraph>
            Enter the URL to a YAML template file to import it in {appTitle}.
          </Typography>
          <Typography paragraph>
            The wizard analyzes the file, previews the templates, and adds them
            to {appTitle}. You can upload templates of the following kinds:{' '}
            <Typography component="span" style={{ fontWeight: 700 }}>
              {Array.from(KIND_TO_IMPORT).join(', ')}
            </Typography>{' '}
            . These YAML files must be uploaded to a specific repository of
            which {appTitle} has access. Each repository can have multiple YAML
            files, and for each one of them you have to upload it separately.
          </Typography>
        </>
      )}
      stepper={stepper}
    />
  );
};
